import { useEffect, useState } from "react";
import { BookMarked, Plus, Pencil, Trash2, Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

function Item({ e, onSaved, onDeleted }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/evidence/${e.id}`, edit);
      onSaved(data); setEdit(null); toast.success("Saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save. Please try again."); } finally { setBusy(false); }
  };
  const del = async () => {
    setBusy(true);
    try { await api.delete(`/evidence/${e.id}`); onDeleted(e.id); toast.success(`Removed "${e.skill}" from your library`); }
    catch { toast.error("Couldn't remove it. Please try again."); setBusy(false); }
  };
  if (edit) return (
    <li className="py-4 space-y-2" data-testid={`evidence-lib-edit-${e.id}`}>
      <input aria-label="Skill" value={edit.skill} onChange={(ev) => setEdit((x) => ({ ...x, skill: ev.target.value }))} className="gi-input" />
      <textarea aria-label="Example" rows={2} value={edit.example} onChange={(ev) => setEdit((x) => ({ ...x, example: ev.target.value }))} className="gi-input resize-y" />
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} className="gi-btn gi-btn-primary gi-btn-sm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
        <button onClick={() => setEdit(null)} className="gi-btn gi-btn-secondary gi-btn-sm"><X className="w-4 h-4" /> Cancel</button>
      </div>
    </li>
  );
  return (
    <li className="py-4 flex gap-3 items-start" data-testid={`evidence-lib-item-${e.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-ink">{e.skill}</div>
        {e.example ? <p className="text-[15px] text-body mt-0.5">{e.example}</p> : <p className="text-sm text-warning mt-0.5">No example yet. Add one so you're ready if an interviewer asks.</p>}
        <p className="text-xs text-faint mt-1">{e.source === "manual" ? "Added by you" : `Confirmed for ${e.source_app?.role || "an application"}${e.source_app?.company ? ` at ${e.source_app.company}` : ""}`}</p>
      </div>
      {confirmDel ? (
        <div className="flex gap-1.5 shrink-0">
          <button onClick={del} disabled={busy} data-testid={`evidence-lib-delete-confirm-${e.id}`} className="gi-btn gi-btn-danger gi-btn-sm">Remove</button>
          <button onClick={() => setConfirmDel(false)} className="gi-btn gi-btn-secondary gi-btn-sm">Keep</button>
        </div>
      ) : (
        <div className="flex gap-1 shrink-0">
          <button aria-label={`Edit ${e.skill}`} data-testid={`evidence-lib-edit-btn-${e.id}`} onClick={() => setEdit({ skill: e.skill, example: e.example || "" })} className="w-10 h-10 rounded-lg flex items-center justify-center text-body hover:bg-subtle"><Pencil className="w-4 h-4" /></button>
          <button aria-label={`Remove ${e.skill}`} data-testid={`evidence-lib-delete-${e.id}`} onClick={() => setConfirmDel(true)} className="w-10 h-10 rounded-lg flex items-center justify-center text-body hover:text-danger hover:bg-danger-soft"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
    </li>
  );
}

export default function EvidenceLibrary() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ skill: "", example: "" });
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/evidence").then(({ data }) => setItems(data)).catch(() => { setItems([]); toast.error("Couldn't load your library."); }); }, []);
  const add = async () => {
    if (!form.skill.trim()) return toast.error("Add the skill name.");
    if (form.example.trim().length < 10) return toast.error("Add a short real example (at least a few words).");
    setAdding(true);
    try {
      const { data } = await api.post("/evidence", form);
      setItems((x) => [data, ...x]); setForm({ skill: "", example: "" }); setOpen(false);
      toast.success(`"${data.skill}" saved. We'll use it in future applications.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save. Please try again."); } finally { setAdding(false); }
  };
  return (
    <div data-testid="evidence-library">
      <div className="gi-card p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-xl">
            <h2 className="font-display text-lg font-bold text-ink flex items-center gap-2"><BookMarked className="w-5 h-5 text-brand" /> Evidence Library</h2>
            <p className="text-[15px] text-body mt-1">Skills and real examples you've confirmed. When a new job asks for one of these, we add it automatically and label it "From your library". You can still remove it for any application.</p>
          </div>
          {!open && <button data-testid="evidence-lib-add-open" onClick={() => setOpen(true)} className="gi-btn gi-btn-secondary gi-btn-sm"><Plus className="w-4 h-4" /> Add a skill</button>}
        </div>
        {open && (
          <div className="mt-5 rounded-xl border border-line bg-canvas p-4 space-y-3" data-testid="evidence-lib-form">
            <div><label htmlFor="ev-skill" className="gi-label">Skill</label><input id="ev-skill" data-testid="evidence-lib-skill-input" value={form.skill} onChange={(e) => setForm((f) => ({ ...f, skill: e.target.value }))} placeholder="e.g. Budget management" className="gi-input" /></div>
            <div><label htmlFor="ev-example" className="gi-label">A real example</label><textarea id="ev-example" data-testid="evidence-lib-example-input" rows={2} value={form.example} onChange={(e) => setForm((f) => ({ ...f, example: e.target.value }))} placeholder="Where and how you've used it, e.g. Tracked a monthly warehouse budget at FreightCo" className="gi-input resize-y" /></div>
            <div className="flex gap-2">
              <button data-testid="evidence-lib-add-button" onClick={add} disabled={adding} className="gi-btn gi-btn-primary gi-btn-sm">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Save to library</button>
              <button onClick={() => setOpen(false)} className="gi-btn gi-btn-secondary gi-btn-sm">Cancel</button>
            </div>
          </div>
        )}
        {items === null ? <Loader2 className="w-5 h-5 animate-spin text-faint mt-5" /> : items.length === 0 ? (
          <p className="mt-5 text-[15px] text-faint" data-testid="evidence-lib-empty">Nothing here yet. When you tap "I have this" in the Evidence Checker, the skill is saved here for next time.</p>
        ) : (
          <ul className="mt-2 divide-y divide-line">
            {items.map((e) => <Item key={e.id} e={e} onSaved={(d) => setItems((x) => x.map((i) => (i.id === d.id ? d : i)))} onDeleted={(id) => setItems((x) => x.filter((i) => i.id !== id))} />)}
          </ul>
        )}
      </div>
    </div>
  );
}
